import { useState, useEffect } from 'react';
import { Dimensions } from 'react-native';
import { MenuItem } from './types';

export const useSideBarResponsive = (
  isCanvasMode: boolean,
  screenSize?: { width: number; height: number }
) => {
  const [screenDimensions, setScreenDimensions] = useState(() =>
    isCanvasMode && screenSize ? screenSize : Dimensions.get('window')
  );

  useEffect(() => {
    if (!isCanvasMode) {
      const subscription = Dimensions.addEventListener(
        'change',
        ({ window }) => {
          setScreenDimensions(window);
        }
      );
      return () => subscription?.remove();
    }
  }, [isCanvasMode]);

  const effectiveWidth = isCanvasMode
    ? screenSize?.width || 1200
    : screenDimensions.width;

  const isDesktop = effectiveWidth >= 1200;
  const isTablet = effectiveWidth >= 768 && effectiveWidth < 1200;
  const isMobile = effectiveWidth < 768;

  return {
    effectiveWidth,
    isDesktop,
    isTablet,
    isMobile,
    screenDimensions,
  };
};

export const useSideBarCollapse = (initialCollapsed: boolean = false) => {
  const [collapsed, setCollapsed] = useState(initialCollapsed);

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  return {
    collapsed,
    setCollapsed,
    toggleCollapse,
  };
};

export const useSideBarMenuItems = (attributes?: any) => {
  const getMenuItems = (): MenuItem[] => {
    const menuListItems = attributes?.menuList?.items;
    if (
      menuListItems &&
      Array.isArray(menuListItems) &&
      menuListItems.length > 0
    ) {
      return menuListItems.map((item: any, index: number) => ({
        id: item.id || `item-${index}`,
        label: item.text || item.label || `Menu Item ${index + 1}`,
        type: item.type || 'item',
        icon: item.icon || undefined,
        href: item.href || undefined,
        badge: item.badge || undefined,
        action: item.action || undefined, // Include action property
        children: item.children
          ? item.children.map((child: any, childIndex: number) => ({
              id: child.id || `child-${index}-${childIndex}`,
              label:
                child.text || child.label || `Child Item ${childIndex + 1}`,
              type: child.type || 'item',
              icon: child.icon || undefined,
              href: child.href || undefined,
              badge: child.badge || undefined,
              action: child.action || undefined, // Include action property for children too
              disabled: child.disabled || false,
              hidden: child.hidden || false,
              divider: child.divider || false,
            }))
          : undefined,
        disabled: item.disabled || false,
        hidden: item.hidden || false,
        divider: item.divider || false,
      }));
    }

    // Fallback to default menu items
    return [
      { id: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
      { id: 'users', label: 'Users', icon: 'people' },
      { id: 'settings', label: 'Settings', icon: 'settings' },
    ];
  };

  return {
    menuItems: getMenuItems(),
  };
};
